import { SUBAPPS, appCanMountAt, type ScopeLevel, type SubAppManifest } from "@perlab/registry";
import { navTo } from "../components/ContextChain";

/** A navigable target compatible with TanStack `navigate({ to, params })`. */
export interface NavTarget {
  to: string;
  params: Record<string, string>;
}

export interface ShellCommand {
  id: string;
  label: string;
  group: "Navigate" | "Apps";
  target: NavTarget;
}

export interface ShellScope {
  ws?: string;
  client?: string;
  eng?: string;
  project?: string;
}

/**
 * Build the command list from the live shell nav + app registry + current scope.
 * Scope-aware: Home resolves to the deepest available scope home; Apps are offered at the current
 * (deepest present) scope level, filtered by each app's `mountLevels` (V2 multi-level mounting).
 */
export function buildShellCommands(
  scope: ShellScope,
  apps: readonly SubAppManifest[] = SUBAPPS,
): ShellCommand[] {
  const { ws, client, eng, project } = scope;
  const cmds: ShellCommand[] = [];
  if (!ws) return cmds;

  // Engagement/project/app targets are client-scoped (ADR-08): offered only when a
  // client is in scope (it always is when eng is, since eng belongs to a client).
  cmds.push({
    id: "home",
    label: "Home",
    group: "Navigate",
    // Cast: projectHome/engagementHome return a union (client-scoped | client-less host shape),
    // branched on `client` INSIDE the builder (48.23) — so the gates here need only eng/project:
    // a host engagement/project routes to its OWN home, no longer falling back to the workspace's.
    target: (
      eng && project
        ? navTo.projectHome(ws, client ?? "", eng, project)
        : eng
          ? navTo.engagementHome(ws, client ?? "", eng)
          : client
            ? navTo.clientHome(ws, client)
            : navTo.workspaceHome(ws)
    ) as NavTarget,
  });
  cmds.push({ id: "settings", label: "Settings", group: "Navigate", target: navTo.settings(ws) });

  // V2 multi-level mounting: offer each app at the CURRENT (deepest present) scope level, gated by its
  // declared `mountLevels` (default project-only). navTo.app is level-aware (picks the per-level route).
  const level: ScopeLevel = project ? "project" : eng ? "engagement" : client ? "client" : "workspace";
  for (const a of apps) {
    if (!appCanMountAt(a, level)) continue;
    cmds.push({
      id: `app-${a.id}`,
      label: a.name,
      group: "Apps",
      // The cast widens navTo.app's per-level union to the structural NavTarget — every member is
      // {to: string, params: Record<string,string>}; only TS's union→index-signature rule needs the hint.
      target: navTo.app(ws, client ?? "", eng ?? "", project ?? "", a.id) as NavTarget,
    });
  }
  return cmds;
}

/** Case-insensitive substring filter over command labels. Empty query = all. */
export function filterCommands(cmds: ShellCommand[], query: string): ShellCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return cmds;
  return cmds.filter((c) => c.label.toLowerCase().includes(q));
}
